import { useEffect, useRef, useState, type RefObject } from 'react';
import type { GridApi } from '@mui/x-data-grid-premium';
import {
  Box,
  Button,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import Check from '@mui/icons-material/Check';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import DriveFileRenameOutline from '@mui/icons-material/DriveFileRenameOutline';
import SaveAs from '@mui/icons-material/SaveAs';
import Save from '@mui/icons-material/Save';
import Star from '@mui/icons-material/Star';
import StarBorder from '@mui/icons-material/StarBorder';
import { useTranslation } from 'react-i18next';
import {
  applyGridColumns,
  applyGridLayout,
  exportGridLayout,
  readSavedLayout,
  writeSavedLayout,
} from '@/lib/gridState';
import {
  useGridViews,
  useSaveGridView,
  useRenameGridView,
  useDeleteGridView,
  useSetDefaultGridView,
  useClearDefaultGridView,
} from '@/hooks/grid/useGridViews';
import type { UserGridView } from '@/types/core/UserGridView';
import { DialogValidationErrors } from '@/components/forms/DialogValidationErrors';
import { toFormErrors, type ValidationError } from '@/lib/validationErrors';

const SAVE_DEBOUNCE_MS = 300;

interface GridViewSelectorProps {
  apiRef: RefObject<GridApi | null>;
  /** Grid identifier in "Module:Entity" form, e.g. "Sales:Order". */
  gridKey: string;
  /** Label of the built-in system default view (the entity's plural name, e.g. "Orders"). */
  defaultLabel: string;
  /**
   * When the page was opened with an explicit filter (a `?filter.<field>` deep link), the bootstrap
   * restore keeps its columns/sort but must not clobber that filter — an explicit link outranks a
   * saved view's remembered filter.
   */
  preserveActiveFilter?: boolean;
}

/**
 * Page-title dropdown for per-user, DB-persisted saved grid views (D365-style),
 * and the single owner of list-view layout persistence.
 *
 * Open precedence: a starred default view opens; otherwise the system default.
 * Selecting a view is session-only (it never becomes the auto-opened view —
 * only starring does). The system default's unsaved tweaks persist per-device
 * via localStorage; saved-view tweaks are session-only (Save to keep them).
 */
export function GridViewSelector({ apiRef, gridKey, defaultLabel, preserveActiveFilter = false }: GridViewSelectorProps) {
  const { t } = useTranslation();
  const { data: views } = useGridViews(gridKey);
  const saveView = useSaveGridView(gridKey);
  const renameView = useRenameGridView(gridKey);
  const deleteView = useDeleteGridView(gridKey);
  const setDefaultView = useSetDefaultGridView(gridKey);
  const clearDefaultView = useClearDefaultGridView(gridKey);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // Active view is SESSION-ONLY (not persisted) so a selected view never becomes
  // the auto-opened view; opening is decided by the starred default / system default.
  const [activeViewId, setActiveViewIdState] = useState<string | null>(null);
  const [dialog, setDialog] = useState<
    | { mode: 'saveAs'; name: string }
    | { mode: 'rename'; name: string; target: UserGridView }
    | { mode: 'delete'; target: UserGridView }
    | null
  >(null);
  const [apiValidationErrors, setApiValidationErrors] = useState<ValidationError[]>([]);

  // --- persistence machinery ---
  const activeViewIdRef = useRef<string | null>(null); // live mirror for event handlers
  const currentLayoutRef = useRef<any>(null); // layout of the active view (for re-apply)
  const pristineRef = useRef<any>(null); // factory layout (colDef defaults + DSL initialState)
  const isRestoringRef = useRef(false); // suppress our own restoreState echoes
  const userActionRef = useRef(false); // marks a user-driven tick (robust to event order)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bootstrappedRef = useRef(false);

  const setActiveView = (id: string | null) => {
    activeViewIdRef.current = id;
    setActiveViewIdState(id);
  };

  const markUserAction = () => {
    userActionRef.current = true;
    // Cleared on a macrotask — after this tick's columnsChange microtask.
    setTimeout(() => {
      userActionRef.current = false;
    }, 0);
  };

  const applyFull = (layout: any) => {
    if (!layout) return;
    isRestoringRef.current = true;
    try {
      applyGridLayout(apiRef, layout);
    } finally {
      isRestoringRef.current = false;
    }
    currentLayoutRef.current = layout;
  };

  const writeNow = () => {
    // Only the system default persists its working layout per-device.
    if (activeViewIdRef.current == null && currentLayoutRef.current) {
      writeSavedLayout(gridKey, currentLayoutRef.current);
    }
  };

  // Capture the factory layout before anything is applied.
  useEffect(() => {
    pristineRef.current = exportGridLayout(apiRef);
    currentLayoutRef.current = pristineRef.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Decide what opens once the user's views have loaded.
  useEffect(() => {
    if (bootstrappedRef.current || !views) return;
    bootstrappedRef.current = true;

    const def = views.find((v) => v.isDefault);
    let target: any = null;
    let activeId: string | null = null;
    if (def?.state) {
      try {
        target = JSON.parse(def.state);
        activeId = def.id;
      } catch {
        target = null;
      }
    }
    if (target == null) {
      // System default: restore the device's persisted tweaks if any.
      target = readSavedLayout(gridKey);
      activeId = null;
    }
    setActiveView(activeId);

    if (target) {
      if (preserveActiveFilter && target.filter) {
        // The page was opened with an explicit deep-link filter — keep the saved view's
        // columns/sort but leave the filter model alone.
        target = { ...target };
        delete target.filter;
      }
      // Set synchronously so the columnsChange re-apply uses the right layout…
      currentLayoutRef.current = target;
      // …but defer the actual restore to a microtask so it runs AFTER the grid's
      // own mount effects have registered columns. Applying too early (this is a
      // child rendered before the grid) drops the column dimensions (width).
      queueMicrotask(() => applyFull(target));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [views]);

  // Subscribe to grid events: persist on change, and re-assert the current
  // view's columns when a column rebuild (async enum/restriction settle) resets
  // width/order. Deferred to a microtask + a user-action guard so user gestures
  // (resize, reorder, grouping, …) are never reverted.
  useEffect(() => {
    const api = apiRef.current;
    if (!api?.subscribeEvent) return;

    const saveEvents = [
      'columnVisibilityModelChange',
      'columnOrderChange',
      'sortModelChange',
      'filterModelChange',
      'columnWidthChange',
      'columnResizeStop',
      'paginationModelChange',
      'densityChange',
      'rowGroupingModelChange',
      'aggregationModelChange',
      'pinnedColumnsChange',
      'pivotModeChange',
      'pivotModelChange',
    ] as const;
    const markOnlyEvents = ['columnIndexChange'] as const;

    const onUserChange = () => {
      if (isRestoringRef.current) return;
      markUserAction();
      const layout = exportGridLayout(apiRef);
      if (layout) currentLayoutRef.current = layout;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(writeNow, SAVE_DEBOUNCE_MS);
    };

    const unsubscribers: Array<() => void> = [];
    for (const event of saveEvents) {
      unsubscribers.push(api.subscribeEvent(event as any, onUserChange));
    }
    for (const event of markOnlyEvents) {
      unsubscribers.push(api.subscribeEvent(event as any, markUserAction));
    }
    unsubscribers.push(
      api.subscribeEvent('columnsChange' as any, () => {
        if (isRestoringRef.current) return;
        queueMicrotask(() => {
          if (userActionRef.current || !currentLayoutRef.current) return;
          isRestoringRef.current = true;
          try {
            applyGridColumns(apiRef, currentLayoutRef.current);
          } finally {
            isRestoringRef.current = false;
          }
        });
      }),
    );

    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        writeNow();
      }
      unsubscribers.forEach((unsub) => unsub());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRef, gridKey]);

  const views_ = views ?? [];
  const activeView = views_.find((v) => v.id === activeViewId) ?? null;
  const isDefaultActive = activeView == null;
  const currentLabel = activeView?.name ?? defaultLabel;

  const closeMenu = () => setAnchorEl(null);

  const selectSystemDefault = () => {
    applyFull(readSavedLayout(gridKey) ?? pristineRef.current);
    setActiveView(null);
    closeMenu();
  };

  const applyView = (view: UserGridView) => {
    if (view.state) {
      try {
        applyFull(JSON.parse(view.state));
      } catch {
        /* malformed — ignore */
      }
    }
    setActiveView(view.id);
  };

  // The system default name is reserved — users must pick a different name.
  const isReservedName = (name: string) =>
    name.trim().toLowerCase() === defaultLabel.trim().toLowerCase();

  const reservedNameError = () =>
    setApiValidationErrors([
      {
        field: 'name',
        message: t('grid.views.reservedName', {
          defaultValue: 'That name is reserved for the system default view. Choose a different name.',
        }),
        instancePath: '#',
        members: [],
      },
    ]);

  const runMutation = async (fn: () => Promise<void>) => {
    try {
      setApiValidationErrors([]);
      await fn();
    } catch (error: unknown) {
      setApiValidationErrors(toFormErrors(error, t('common.errorOccurred', { defaultValue: 'An error occurred' })));
    }
  };

  const handleSaveCurrent = () =>
    runMutation(async () => {
      if (!activeView) return;
      const layout = exportGridLayout(apiRef);
      await saveView.mutateAsync({
        name: activeView.name,
        state: JSON.stringify(layout),
        isDefault: activeView.isDefault,
      });
    });

  const handleSaveAs = () =>
    runMutation(async () => {
      if (!dialog || dialog.mode !== 'saveAs') return;
      const name = dialog.name.trim();
      if (!name) return;
      if (isReservedName(name)) {
        reservedNameError();
        return;
      }
      const layout = exportGridLayout(apiRef);
      const saved = await saveView.mutateAsync({ name, state: JSON.stringify(layout) });
      setActiveView(saved.id);
      setDialog(null);
    });

  const handleRename = () =>
    runMutation(async () => {
      if (!dialog || dialog.mode !== 'rename') return;
      const name = dialog.name.trim();
      if (!name) return;
      if (isReservedName(name)) {
        reservedNameError();
        return;
      }
      await renameView.mutateAsync({ id: dialog.target.id, name });
      setDialog(null);
    });

  const handleDelete = () =>
    runMutation(async () => {
      if (!dialog || dialog.mode !== 'delete') return;
      await deleteView.mutateAsync(dialog.target.id);
      if (activeViewId === dialog.target.id) selectSystemDefault();
      setDialog(null);
    });

  // Toggle: starring sets the default; clicking the filled star clears it so the
  // system default opens for the grid.
  const handleToggleDefault = (view: UserGridView) =>
    runMutation(async () => {
      if (view.isDefault) {
        await clearDefaultView.mutateAsync(view.id);
        if (activeViewId === view.id) selectSystemDefault();
      } else {
        await setDefaultView.mutateAsync(view.id);
      }
    });

  return (
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
      {/*
        This selector IS the page title, so it has to stay a real heading. Rendering it as a
        <span> inside the <button> (which is what it was) left every list page in the app with
        ZERO h1–h6 and no role=heading: it looked like an h5 but carried no heading semantics, so
        screen-reader users had nothing to jump to and `getByRole('heading')` found nothing.

        A heading may contain a button (a button is phrasing content) but a button may NOT contain
        a heading — so the fix is to wrap, not to restyle. `m: 0` keeps the rendered layout
        byte-identical; all visual styling still comes from the inner Typography.
        h1 is the right level: the list page has no other heading above this one.
      */}
      <Box component="h1" sx={{ m: 0, minWidth: 0, maxWidth: '100%' }}>
        <Button
          onClick={(e) => setAnchorEl(e.currentTarget)}
          endIcon={<ArrowDropDown />}
          sx={{
            textTransform: 'none',
            color: 'text.primary',
            px: 0,
            minWidth: 0,
            maxWidth: '100%',
            '&:hover': { backgroundColor: 'transparent' },
          }}
        >
          <Typography variant="h5" component="span" sx={{ fontWeight: 500 }} noWrap>
            {currentLabel}
          </Typography>
        </Button>
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        {/* System default */}
        <MenuItem selected={isDefaultActive} onClick={selectSystemDefault}>
          <ListItemIcon>
            {isDefaultActive ? <Check fontSize="small" /> : <Box sx={{ width: 20 }} />}
          </ListItemIcon>
          <ListItemText
            primary={defaultLabel}
            secondary={t('grid.views.systemDefault', { defaultValue: 'System default' })}
          />
        </MenuItem>

        {views_.length > 0 && <Divider />}

        {views_.map((view) => (
          <MenuItem
            key={view.id}
            selected={view.id === activeViewId}
            onClick={() => {
              applyView(view);
              closeMenu();
            }}
            sx={{
              '& .gv-actions': { opacity: 0, pointerEvents: 'none', transition: 'opacity 0.15s' },
              '&:hover .gv-actions, &.Mui-focusVisible .gv-actions': {
                opacity: 1,
                pointerEvents: 'auto',
              },
            }}
          >
            <ListItemIcon>
              {view.id === activeViewId ? <Check fontSize="small" /> : <Box sx={{ width: 20 }} />}
            </ListItemIcon>
            <ListItemText primary={view.name} />
            {/* The default star stays visible so the default is identifiable without hovering. */}
            {view.isDefault && (
              <Tooltip
                title={t('grid.views.unsetDefault', {
                  defaultValue: 'Default view — click to unset (system default opens instead)',
                })}
              >
                <Box
                  component="span"
                  sx={{ ml: 1, display: 'inline-flex' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleDefault(view);
                  }}
                >
                  <Star fontSize="small" color="warning" />
                </Box>
              </Tooltip>
            )}
            <Box
              className="gv-actions"
              sx={{ ml: view.isDefault ? 0.5 : 1, display: 'inline-flex', alignItems: 'center' }}
            >
              {!view.isDefault && (
                <Tooltip title={t('grid.views.setDefault', { defaultValue: 'Set as default' })}>
                  <Box
                    component="span"
                    sx={{ display: 'inline-flex' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleDefault(view);
                    }}
                  >
                    <StarBorder fontSize="small" />
                  </Box>
                </Tooltip>
              )}
              <Tooltip title={t('common.rename', { defaultValue: 'Rename' })}>
                <Box
                  component="span"
                  sx={{ ml: 0.5, display: 'inline-flex' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDialog({ mode: 'rename', name: view.name, target: view });
                  }}
                >
                  <DriveFileRenameOutline fontSize="small" />
                </Box>
              </Tooltip>
              <Tooltip title={t('common.delete', { defaultValue: 'Delete' })}>
                <Box
                  component="span"
                  sx={{ ml: 0.5, display: 'inline-flex' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDialog({ mode: 'delete', target: view });
                  }}
                >
                  <DeleteOutline fontSize="small" />
                </Box>
              </Tooltip>
            </Box>
          </MenuItem>
        ))}

        <Divider />

        {activeView && (
          <MenuItem
            onClick={() => {
              handleSaveCurrent();
              closeMenu();
            }}
          >
            <ListItemIcon>
              <Save fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t('grid.views.save', { defaultValue: 'Save changes to this view' })}
            />
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setDialog({ mode: 'saveAs', name: isDefaultActive ? '' : currentLabel });
            closeMenu();
          }}
        >
          <ListItemIcon>
            <SaveAs fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t('grid.views.saveAs', { defaultValue: 'Save current as new view…' })}
          />
        </MenuItem>
      </Menu>

      {/* Save as / Rename dialog */}
      <Dialog
        open={dialog?.mode === 'saveAs' || dialog?.mode === 'rename'}
        onClose={() => setDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {dialog?.mode === 'rename'
            ? t('grid.views.renameTitle', { defaultValue: 'Rename view' })
            : t('grid.views.saveAsTitle', { defaultValue: 'Save view' })}
        </DialogTitle>
        <DialogContent>
          <DialogValidationErrors
            errors={apiValidationErrors}
            onClear={() => setApiValidationErrors([])}
          />
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label={t('grid.views.nameLabel', { defaultValue: 'View name' })}
            value={dialog && dialog.mode !== 'delete' ? dialog.name : ''}
            onChange={(e) =>
              setDialog((d) => (d && d.mode !== 'delete' ? { ...d, name: e.target.value } : d))
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (dialog?.mode === 'rename') handleRename();
                else handleSaveAs();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button
            variant="contained"
            onClick={() => (dialog?.mode === 'rename' ? handleRename() : handleSaveAs())}
          >
            {t('common.save', { defaultValue: 'Save' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={dialog?.mode === 'delete'} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('grid.views.deleteTitle', { defaultValue: 'Delete view' })}</DialogTitle>
        <DialogContent>
          {t('grid.views.deleteConfirm', {
            defaultValue: 'Delete the view "{{name}}"? This cannot be undone.',
            name: dialog?.mode === 'delete' ? dialog.target.name : '',
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            {t('common.delete', { defaultValue: 'Delete' })}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
